import { useState } from 'react';
import { Button, useConfirm } from '../components/ui';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { can } from '@gwc/shared';
import { winesApi } from '../api/wines';
import type { WineAppearance } from '../api/models';
import { useAuth } from '../auth/AuthProvider';
import { StatTile } from '../components/Widgets';
import { WineColorChip } from '../components/WineColorChip';
import { WineFormDialog } from '../components/WineFormDialog';
import { ApiError } from '../lib/api';

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ARCHIVED = new Set(['completed', 'archived']);

export function WineDetail() {
  const confirm = useConfirm();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = user ? can(user.roles, 'records:contribute') : false;
  const canDelete = user ? can(user.roles, 'records:approve') : false;
  const [editing, setEditing] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['wine', id],
    queryFn: () => winesApi.get(id),
  });

  const del = useMutation({
    mutationFn: () => winesApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wines'] });
      navigate('/cellar');
    },
  });

  if (isLoading) return <p className="text-cream-300/60">Loading…</p>;
  if (error instanceof ApiError && error.status === 404) return <p className="text-cream-300/60">Wine not found.</p>;
  if (!data) return <p className="text-cream-300/60">Could not load this wine.</p>;

  const { wine, appearances, stats } = data;
  const place = [wine.region, wine.country].filter(Boolean).join(' · ');
  const drink =
    wine.drinkFrom || wine.drinkTo
      ? `${wine.drinkFrom ?? '?'}–${wine.drinkTo ?? '?'}`
      : null;

  return (
    <div>
      <Link to="/cellar" className="text-xs text-cream-300/60 hover:text-gold-200">
        ← The Cellar
      </Link>

      <div className="mt-3 flex flex-col gap-6 sm:flex-row">
        {wine.labelImageUrl ? (
          <img
            src={wine.labelImageUrl}
            alt={wine.wineName ?? 'Wine label'}
            className="h-52 w-40 shrink-0 rounded-lg border border-ink-800 object-cover"
            onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
          />
        ) : (
          <div className="grid h-52 w-40 shrink-0 place-items-center rounded-lg border border-ink-800 bg-ink-950 font-display text-5xl text-gold-400/50">
            {(wine.wineName ?? '?').charAt(0)}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-3xl text-cream-50">
              {wine.vintage ? `${wine.vintage} ` : ''}
              {wine.wineName ?? 'Unnamed wine'}
            </h1>
            <WineColorChip color={wine.color} />
          </div>
          {wine.producer && <p className="mt-1 text-lg text-gold-200">{wine.producer}</p>}
          {place && <p className="mt-0.5 text-sm text-cream-300/60">{place}</p>}
          {wine.varietals.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {wine.varietals.map((v, i) => (
                <span
                  key={v}
                  className={`rounded-full border px-2 py-0.5 text-xs ${
                    i === 0 ? 'border-gold-600/40 text-gold-200' : 'border-ink-800 text-cream-300/60'
                  }`}
                >
                  {v}
                </span>
              ))}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-cream-300/70">
            {wine.vineyard && <span>Vineyard: <span className="text-cream-100">{wine.vineyard}</span></span>}
            {wine.abv && <span>ABV: <span className="text-cream-100">{wine.abv}%</span></span>}
            {wine.typicalPrice && <span>Price: <span className="text-cream-100">${wine.typicalPrice}</span></span>}
            {drink && <span>Drink: <span className="text-cream-100">{drink}</span></span>}
          </div>

          {(canEdit || canDelete) && (
            <div className="mt-5 flex gap-2">
              {canEdit && (
                <button className="btn-ghost text-sm" onClick={() => setEditing(true)}>
                  Edit
                </button>
              )}
              {canDelete && (
                <Button
                  variant="danger"
                  className="text-sm"
                  loading={del.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Remove this wine from the Cellar?',
                      description: 'Past tastings keep their record — only the catalog entry goes.',
                      confirmLabel: 'Remove wine',
                      destructive: true,
                    });
                    if (ok) del.mutate();
                  }}
                >
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Times poured" value={stats.timesPoured} />
        <StatTile label="Avg group score" value={stats.avgScore != null ? stats.avgScore.toFixed(1) : '—'} accent="wine" />
        <StatTile label="Best finish" value={stats.bestRank != null ? `#${stats.bestRank}` : '—'} />
      </div>

      {wine.tastingNotes && (
        <div className="card mt-6">
          <h2 className="mb-2 text-sm uppercase tracking-wide text-gold-400">Tasting notes</h2>
          <p className="whitespace-pre-wrap text-sm text-cream-200">{wine.tastingNotes}</p>
        </div>
      )}

      <div className="card mt-6">
        <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">
          Tastings ({appearances.length})
        </h2>
        {appearances.length === 0 ? (
          <p className="text-sm text-cream-300/50">
            Not yet poured at a tasting. Link it from a tasting's setup to connect it here.
          </p>
        ) : (
          <ul className="divide-y divide-ink-800">
            {appearances.map((a) => (
              <Appearance key={a.wineId} a={a} />
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <WineFormDialog
          editing={{ ...wine, id }}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
    </div>
  );
}

function Appearance({ a }: { a: WineAppearance }) {
  const to = ARCHIVED.has(a.eventStatus) ? `/archive/${a.eventId}` : `/tastings/${a.eventId}`;
  const when = a.eventMonth ? `${MONTHS[a.eventMonth]} ${a.eventYear}` : String(a.eventYear);
  const broughtBy = a.broughtByFirst ? `${a.broughtByFirst} ${a.broughtByLast ?? ''}`.trim() : null;
  return (
    <li className="py-2.5">
      <Link to={to} className="flex items-center justify-between gap-3 hover:text-gold-200">
        <div className="min-w-0">
          <div className="truncate text-cream-100">
            <span className="font-display text-gold-300">{a.letter}</span> · {a.eventTitle}
          </div>
          <div className="text-xs text-cream-300/50">
            {when}
            {broughtBy && ` · brought by ${broughtBy}`}
          </div>
        </div>
        <div className="shrink-0 text-right text-xs text-cream-300/60">
          {a.groupRank != null && <div className="text-gold-300">#{a.groupRank}</div>}
          {a.groupAverage != null && <div>avg {Number(a.groupAverage).toFixed(1)}</div>}
        </div>
      </Link>
    </li>
  );
}
